// GitHub Contents API: token/repo storage, report fetch, response submit.

import * as yaml from 'js-yaml'

const STORAGE_TOKEN = 'correspond_gh_token'
const STORAGE_REPO  = 'correspond_gh_repo'
const API_BASE      = 'https://api.github.com'

export const getToken     = ()  => localStorage.getItem(STORAGE_TOKEN) ?? ''
export const setToken     = t   => localStorage.setItem(STORAGE_TOKEN, t.trim())
export const getRepo      = ()  => localStorage.getItem(STORAGE_REPO)  ?? ''
export const setRepo      = r   => localStorage.setItem(STORAGE_REPO,  r.trim())
export const isConfigured = ()  => Boolean(getToken() && getRepo())

export async function testConnection(token, repo) {
  const res = await fetch(`${API_BASE}/repos/${repo}/contents/outbox`, {
    headers: { Authorization: `token ${token}`, Accept: 'application/vnd.github+json' },
  })
  if (res.status === 404) return 'OK (outbox not yet created)'
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.message ?? `HTTP ${res.status}`)
  }
  const files = await res.json()
  const count = Array.isArray(files) ? files.filter(f => /\.ya?ml$/i.test(f.name)).length : 0
  return `OK — ${count} report${count !== 1 ? 's' : ''} in outbox`
}

function _headers() {
  return {
    Authorization: `token ${getToken()}`,
    Accept: 'application/vnd.github+json',
  }
}

// List reports in outbox/, newest first. Returns metadata without fetching content.
export async function listReports() {
  const repo = getRepo()
  const res = await fetch(`${API_BASE}/repos/${repo}/contents/outbox`, { headers: _headers() })

  if (res.status === 404) return []
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.message ?? `GitHub API error ${res.status}`)
  }

  const files = await res.json()
  return files
    .filter(f => f.type === 'file' && /\.ya?ml$/i.test(f.name))
    .sort((a, b) => b.name.localeCompare(a.name))
    .map(f => {
      const stem  = f.name.replace(/\.ya?ml$/i, '')
      // Filename format: 20260326T120000-project-slug-words
      const m = stem.match(/^(\d{8}T\d{6})-([^-]+)-(.+)$/)
      return {
        name:    f.name,
        path:    f.path,   // used with Contents API — download_url is null for private repos
        project: m ? m[2] : '',
        slug:    m ? m[3].replace(/-/g, ' ') : stem,
        date:    m ? _fmtDate(m[1]) : '',
      }
    })
}

// Fetch the raw YAML for a specific report by its repo path.
export async function fetchReport(path) {
  const repo = getRepo()
  const res = await fetch(`${API_BASE}/repos/${repo}/contents/${path}`, { headers: _headers() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.message ?? `HTTP ${res.status} fetching ${path}`)
  }
  const data = await res.json()
  // Contents API returns base64-encoded content (private repos have no download_url)
  return decodeURIComponent(escape(atob(data.content.replace(/\s/g, ''))))
}

function _fmtDate(ts) {
  // "20260326T120000" → "Mar 26, 12:00"
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const mo = parseInt(ts.slice(4, 6)) - 1
  const d  = parseInt(ts.slice(6, 8))
  const h  = ts.slice(9, 11), min = ts.slice(11, 13)
  return `${months[mo]} ${d}, ${h}:${min}`
}

// Serialize responses and push to inbox/. Fire-and-forget — caller does not await.
export async function submitResponse(reportId, project, responses) {
  const repo = getRepo()

  const items = Object.entries(responses)
    .filter(([, v]) => v.transcript || v.selected)
    .map(([sectionId, v]) => {
      const item = {
        ref:        sectionId,
        kind:       v.selected != null ? 'answer' : 'comment',
        transcript: v.transcript ?? '',
      }
      if (v.selected != null) item.selected = v.selected
      return item
    })

  const doc = {
    type:      'correspond-response',
    version:   '1',
    report_id: reportId,
    project,
    completed: new Date().toISOString(),
    items,
  }

  // btoa only handles Latin-1; encodeURIComponent+unescape handles UTF-8 safely.
  const content = btoa(unescape(encodeURIComponent(yaml.dump(doc))))

  const filename = `${reportId}.response.yaml`

  const res = await fetch(`${API_BASE}/repos/${repo}/contents/inbox/${filename}`, {
    method:  'PUT',
    headers: { ..._headers(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({ message: `response: ${reportId}`, content }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.message ?? `GitHub submit error ${res.status}`)
  }
}
